import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { usePagination } from '../../hooks/usePagination'
import { useApi } from '../../hooks/useApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { TypeVas } from '../../interfaces/TypeVas/TypeVas'
import { ApiResponse } from '../../typings/ApiResponse'
import { deleteTypeVas, getTypeVas } from './config/api'
import { Typography } from '@mui/material'
import { Edit, Delete } from '@mui/icons-material'
import { Status } from '../../hooks/useFetchState'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { getColumnsConfig } from './config/tableColumns'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { EditTypeVasModal } from './modal/EditTypeVasModal'
import { CreateTypeVasModal } from './modal/CreateTypeVasModal'

interface TypeVasPageProps {}

export const TypeVasPage: React.FC<TypeVasPageProps> = () => {
  const { callApi, state } = useApi<ApiResponse<TypeVas>>()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const pagination = usePagination(10)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const [orderBy, setOrderBy] = useState<GenericOrderBy<TypeVas>>({
    field: 'supplierName' as keyof TypeVas,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const handleSorting = (field: keyof TypeVas, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    callApi(getTypeVas(filters, page, pageSize, orderBy)).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const handleDelete = (id: number) => {
    callApi(deleteTypeVas(id))
      .then(() => {
        openSnackBar('Tipo de VAS eliminado correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        deleteModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestData(selectedFilters, pagination.page, pagination.pageSize)
  }, [orderBy, selectedFilters])

  const deleteModal = useModal<TypeVas>('Confirmar Eliminación')
  const editModal = useModal<TypeVas>('Editar Tipo VAS')
  const createModal = useModal<TypeVas>('Crear Tipo VAS')

  const handleCreateClick = () => {
    createModal.openModal('sm')
  }

  return (
    <Fragment>
      <StandardPage
        pageTitle=''
      >
        <FilterComponent
          options={filterOptions}
          onSearchClick={() => {}}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip='Crear Tipo VAS'
        />
        <GenericTableV2<TypeVas>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='typeVasId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof TypeVas,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('sm', item),
              icon: <Edit />
            },
            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        loading={state.status === Status.Loading}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.typeVasId || 0),
          title: 'Confirmar'
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
      >
        <Typography>¿Desea eliminar el Tipo de VAS: <strong>{deleteModal.extendedProps?.name}</strong>?</Typography>
      </Modal>
      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
      >
        <EditTypeVasModal
          typeVas={editModal.extendedProps as TypeVas}
          handleSnackbar={openSnackBar}
          handleCloseModal={editModal.closeModal}
          requestTypeVas={() => {
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }}
        />
      </Modal>
      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
      >
        <CreateTypeVasModal
          handleSnackbar={openSnackBar}
          handleCloseModal={createModal.closeModal}
          requestTypeVas={() => {
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }}
        />
      </Modal>
      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
